import type { CoverageLabel, DisplayStatus, PlanId } from "./types";

export function formatCHF(rappen: number): string {
  return new Intl.NumberFormat("de-CH", {
    style: "currency",
    currency: "CHF",
  }).format(rappen / 100);
}

export function formatDate(unixSeconds: number): string {
  return new Date(unixSeconds * 1000).toLocaleDateString("de-CH", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** "2026-01-05" -> "05.01.2026" */
export function formatIsoDate(iso: string): string {
  const [y, m, d] = iso.split("-");
  return `${d}.${m}.${y}`;
}

/**
 * Period ends are stored as exclusive upper bounds (the moment the next
 * period begins). For humans that's confusing — "bis 10.07" reads as
 * "including July 10". This helper subtracts a day so the displayed last
 * day is the actual inclusive last day of the period.
 */
export function formatPeriodEnd(unixSeconds: number): string {
  return formatDate(unixSeconds - 86400);
}

export function formatPeriodRange(start: number, end: number): string {
  return `${formatDate(start)} – ${formatPeriodEnd(end)}`;
}

/** Today's date as YYYY-MM-DD (local). */
export function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function monthsLabel(months: number): string {
  if (months === 12) return "1 Jahr";
  if (months === 24) return "2 Jahre";
  if (months === 1) return "1 Monat";
  return `${months} Monate`;
}

export const STATUS_LABELS: Record<DisplayStatus, string> = {
  entwurf: "Entwurf",
  offen: "Offen",
  teilbezahlt: "Teilbezahlt",
  ueberfaellig: "Überfällig",
  bezahlt: "Bezahlt",
  storniert: "Storniert",
  abgeschrieben: "Abgeschrieben",
  stornorechnung: "Stornorechnung",
};

export const PLAN_LABELS: Record<PlanId, string> = {
  frei: "Frei",
  klein: "Klein",
  mittel: "Mittel",
  gross: "Gross",
  xl: "XL",
};

export function planLabel(id: PlanId): string {
  return PLAN_LABELS[id] ?? id;
}

/** Cap as a human string; null cap = unlimited. */
export function capLabel(cap: number | null): string {
  return cap == null ? "unbegrenzt" : cap.toLocaleString("de-CH");
}

export const PAYMENT_METHOD_LABELS: Record<string, string> = {
  credit: "Mitgliederguthaben",
  bank: "Bank",
  twint: "TWINT",
  cash: "Bar",
  other: "Andere",
};

/** The honest member-panel line. */
export function coverageLabelText(label: CoverageLabel): string {
  switch (label.type) {
    case "beitragsfrei":
      return "beitragsfrei";
    case "bezahlt_bis":
      return `bezahlt bis ${formatPeriodEnd(label.until)}`;
    case "verrechnet_bis": {
      const state =
        label.worst === "ueberfaellig"
          ? "Rechnung überfällig"
          : label.worst === "teilbezahlt"
            ? "Rechnung teilbezahlt"
            : "Rechnung offen";
      return `verrechnet bis ${formatPeriodEnd(label.until)} — ${state}`;
    }
    case "keine":
      return "noch nicht verrechnet";
  }
}
